import React, { useState } from 'react';
import {
    Box,
    Button,
    Typography,
    RadioGroup,
    FormControlLabel,
    Radio,
    Checkbox,
    TextField,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import QaDecision from '@tymely/features/Ticket/QaMode/QaResolutionStep/QaDecision';
import { useSubmitQaResponse } from '@tymely/features/Ticket/QaMode/services';
import { useCreateTicketCrumb, useSelectedComment, useTicketOfflineQuery } from '@tymely/services';
import { IComment, IDineshTicketOperations } from '@tymely/atoms';

export const QaResolutionStep = () => {
    const selectedComment = useSelectedComment();
    const [isFlagging, setIsFlagging] = useState(false);
    const [severity, setSeverity] = useState('');
    const [flagReason, setFlagReason] = useState<string[]>([]);
    const [comments, setComments] = useState('');
    const isSendCompleteEnabled = severity !== '' && flagReason.length > 0;
    const offlineTicketQuery = useTicketOfflineQuery(undefined, false);
    const createTicketCrumb = useCreateTicketCrumb();

    const submitQaMutation = useSubmitQaResponse((selectedComment as IComment).id);

    const handleFlagIssue = () => {
        setIsFlagging(true);
    };

    const handleSeverityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSeverity(event.target.value);
    };

    const handleFlagReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setFlagReason((prev) => (prev.includes(value) ? prev.filter((reason) => reason !== value) : [...prev, value]));
    };

    const handleSendComplete = () => {
        const passed = !isFlagging;
        const qa_notes = passed
            ? ''
            : `Severity: ${severity}; Reasons: ${flagReason.join(', ')}; Comments: ${comments}`;
        createTicketCrumb(
            passed
                ? IDineshTicketOperations.QA_USER_APPROVED_RESOLUTION
                : IDineshTicketOperations.QA_USER_FLAGGED_RESOLUTION,
            {
                qa_notes,
            },
        );
        submitQaMutation.mutate(
            { passed, qa_notes },
            {
                onSuccess: () => {
                    offlineTicketQuery.refetch();
                },
            },
        );
    };

    const severityOptions = [
        { value: 'High', label: 'High (Blocker! Requires urgent action)' },
        { value: 'Medium', label: 'Medium (The issue must be fixed asap)' },
        { value: 'Low', label: 'Low (Minor issue, can be fixed when convenient)' },
    ];

    return (
        <Box>
            {offlineTicketQuery.isFetching ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <QaDecision />
                    <Typography variant="h6" sx={{ mt: 2 }}>
                        Response
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Button variant="contained" color="warning" onClick={handleFlagIssue} sx={{ mr: 2 }}>
                            Flag Issue
                        </Button>
                        <Button variant="contained" color="primary" onClick={handleSendComplete}>
                            Approve
                        </Button>
                    </Box>
                    {isFlagging && (
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="h6">Issue Severity</Typography>
                            <RadioGroup value={severity} onChange={handleSeverityChange}>
                                {severityOptions.map((option) => (
                                    <FormControlLabel
                                        key={option.value}
                                        value={option.value}
                                        control={<Radio sx={{ transform: 'scale(0.8)' }} />}
                                        label={
                                            <Typography variant="body2" sx={{ fontWeight: 400, lineHeight: 1.2 }}>
                                                {option.label}
                                            </Typography>
                                        }
                                    />
                                ))}
                            </RadioGroup>
                            <Typography variant="h6" sx={{ mt: 2 }}>
                                Flag Reason
                            </Typography>
                            <Box>
                                {[
                                    'Message Formatting, Grammar and Syntax',
                                    'Personalization, Tone, and Clarity',
                                    'Information Completeness',
                                    'Action and Situation Alignment',
                                    'Service-Level Appropriateness',
                                    'Links and images',
                                    'Technical Issue',
                                    'Other',
                                ].map((reason) => (
                                    <FormControlLabel
                                        key={reason}
                                        control={
                                            <Checkbox
                                                value={reason}
                                                checked={flagReason.includes(reason)}
                                                onChange={handleFlagReasonChange}
                                                sx={{ transform: 'scale(0.8)', margin: '8px 4px 8px 12px' }}
                                            />
                                        }
                                        label={
                                            <Typography variant="body2" sx={{ fontWeight: 400 }}>
                                                {reason}
                                            </Typography>
                                        }
                                        sx={{ marginBottom: '8px', width: '100%' }}
                                    />
                                ))}
                            </Box>
                            <Typography variant="h6" sx={{ mt: 2 }}>
                                Comments - Optional
                            </Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                value={comments}
                                onChange={(e) => setComments(e.target.value)}
                                placeholder="Add comments here..."
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 2 }}>
                                <Tooltip
                                    title={
                                        !isSendCompleteEnabled
                                            ? severity === ''
                                                ? 'Please select an issue severity'
                                                : flagReason.length === 0
                                                  ? 'Please select at least one flag reason'
                                                  : 'Please select a severity and at least one flag reason'
                                            : ''
                                    }
                                >
                                    <span>
                                        <Button
                                            variant="contained"
                                            color="warning"
                                            disabled={!isSendCompleteEnabled}
                                            onClick={handleSendComplete}
                                        >
                                            Send & Complete
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Box>
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

QaResolutionStep.displayName = 'QaResolutionStep';
